import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { Plus, Search, Pencil, Trash2, Truck, Mail, Phone, User as UserIcon } from "lucide-react";

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await api.get("/suppliers", { params });
      setSuppliers(data.suppliers || []);
    } catch (e) {
      toast.error(extractErr(e, "Failed to load suppliers"));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const onSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, form);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", form);
        toast.success("Supplier created");
      }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const onDelete = async (s) => {
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier removed");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <AppShell
      title="Suppliers"
      subtitle={`${suppliers.length} supplier${suppliers.length === 1 ? "" : "s"}`}
      headerActions={(
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="suppliers-create-button">
          <Plus className="w-4 h-4 mr-1.5" /> New supplier
        </Button>
      )}
    >
      <div className="rounded-2xl border border-border bg-card">
        <div className="p-3 border-b border-border">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search suppliers…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9" data-testid="suppliers-search"
            />
          </div>
        </div>
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : suppliers.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="No suppliers yet"
            description="Create suppliers to use in purchase orders and production orders. Suppliers store contact info you can reuse across the platform."
            action={<Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="suppliers-empty-create"><Plus className="w-4 h-4 mr-1.5" /> Add supplier</Button>}
            className="border-0"
          />
        ) : (
          <div className="divide-y divide-border">
            {suppliers.map((s) => (
              <div key={s.id} className="p-3 sm:p-4 flex items-start gap-3 hover:bg-accent/30" data-testid={`supplier-row-${s.id}`}>
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Truck className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {s.name}
                    {s.currency && s.currency !== "EUR" && (
                      <span className="rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 font-mono" data-testid={`supplier-currency-${s.id}`}>{s.currency}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-0.5 text-xs text-muted-foreground">
                    {s.contact_person && <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {s.contact_person}</span>}
                    {s.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>}
                    {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                    {(s.lead_time_label || s.lead_time_days) && (
                      <span className="flex items-center gap-1 text-primary" data-testid={`supplier-lead-${s.id}`}>
                        ⏱ {s.lead_time_label || `${s.lead_time_days} day${s.lead_time_days === 1 ? "" : "s"}`}
                      </span>
                    )}
                    {s.min_order_qty ? <span>MOQ: {s.min_order_qty}</span> : null}
                    {s.address && <span className="truncate">{s.address}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`supplier-edit-${s.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`supplier-delete-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove supplier?</AlertDialogTitle>
                        <AlertDialogDescription>This will hide “{s.name}” from selection. Past orders remain unchanged.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => onDelete(s)}>Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && <SupplierForm open={open} initial={editing} onClose={() => { setOpen(false); setEditing(null); }} onSave={onSave} />}
    </AppShell>
  );
}

function SupplierForm({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    contact_person: initial?.contact_person || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    address: initial?.address || "",
    website: initial?.website || "",
    tax_id: initial?.tax_id || "",
    currency: (initial?.currency || "EUR").toUpperCase(),
    lead_time_days: initial?.lead_time_days ?? "",
    lead_time_label: initial?.lead_time_label || "",
    min_order_qty: initial?.min_order_qty ?? "",
    notes: initial?.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm({
      name: initial?.name || "",
      contact_person: initial?.contact_person || "",
      email: initial?.email || "",
      phone: initial?.phone || "",
      address: initial?.address || "",
      website: initial?.website || "",
      tax_id: initial?.tax_id || "",
      currency: (initial?.currency || "EUR").toUpperCase(),
      lead_time_days: initial?.lead_time_days ?? "",
      lead_time_label: initial?.lead_time_label || "",
      min_order_qty: initial?.min_order_qty ?? "",
      notes: initial?.notes || "",
    });
  }, [initial]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    // Strip empty optional fields so backend Pydantic validators don't reject empty strings
    const payload = { ...form };
    if (!payload.email) delete payload.email;
    if (!payload.website) delete payload.website;
    // Coerce numerics
    payload.lead_time_days = payload.lead_time_days === "" ? null : Number(payload.lead_time_days);
    payload.min_order_qty = payload.min_order_qty === "" ? null : Number(payload.min_order_qty);
    if (payload.lead_time_days === null) delete payload.lead_time_days;
    if (payload.min_order_qty === null) delete payload.min_order_qty;
    payload.currency = (payload.currency || "EUR").toUpperCase();
    await onSave(payload);
    setSubmitting(false);
  };

  const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "SEK", "NOK", "DKK", "PLN", "CZK", "HUF", "RON", "BGN"];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit supplier" : "New supplier"}</DialogTitle>
          <DialogDescription>Suppliers are reusable across purchase and production orders. Lead time and currency power smarter reordering and cost reports.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Name *</Label>
            <Input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="supplier-form-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Contact person</Label>
            <Input value={form.contact_person} onChange={(e) => update("contact_person", e.target.value)} data-testid="supplier-form-contact" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="supplier-form-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="supplier-form-phone" />
          </div>
          <div className="space-y-1.5">
            <Label>Website</Label>
            <Input type="url" placeholder="https://" value={form.website} onChange={(e) => update("website", e.target.value)} data-testid="supplier-form-website" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="supplier-form-address" />
          </div>

          <div className="md:col-span-2 mt-1 px-3 py-2 rounded-lg bg-primary/5 border border-primary/15 text-xs text-primary">
            Reorder &amp; reporting settings — used to plan reorders earlier and group cost reports.
          </div>

          <div className="space-y-1.5">
            <Label>Standard delivery (numeric, days)</Label>
            <Input type="number" min="0" placeholder="e.g. 14" value={form.lead_time_days}
                   onChange={(e) => update("lead_time_days", e.target.value)} data-testid="supplier-form-lead-days" />
          </div>
          <div className="space-y-1.5">
            <Label>Standard delivery (label)</Label>
            <Input placeholder="e.g. 3-5 working days, 2 weeks, 6 weeks" value={form.lead_time_label}
                   onChange={(e) => update("lead_time_label", e.target.value)} data-testid="supplier-form-lead-label" />
          </div>
          <div className="space-y-1.5">
            <Label>Minimum order qty</Label>
            <Input type="number" step="any" min="0" value={form.min_order_qty}
                   onChange={(e) => update("min_order_qty", e.target.value)} data-testid="supplier-form-moq" />
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.currency}
                    onChange={(e) => update("currency", e.target.value)} data-testid="supplier-form-currency">
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Tax ID / VAT</Label>
            <Input value={form.tax_id} onChange={(e) => update("tax_id", e.target.value)} data-testid="supplier-form-tax" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} />
          </div>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="supplier-form-submit">{submitting ? "Saving…" : initial ? "Save" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
